import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Input, Typography, message } from 'antd';
import { useUpdateGlossaryPropertyMutation } from '../../../../../graphql/glossary.generated';
import { useUpdateEntityPropertyMutation } from '../../../../../graphql/dataset.generated';
import { useUpdateDataProductPropertyMutation } from '../../../../../graphql/dataProduct.generated';
import { useUpdateDomainPropertyMutation } from '../../../../../graphql/domain.generated';
import { useTranslation } from '../../../../../i18n/useTranslation';
import { sanitizeToPlainText } from '../../../../shared/utils/sanitizationUtils';

// Helper function to extract form values from property data
const extractPropertyFormValues = (propertyData) => {
    return {
        key: propertyData.displayName || propertyData.key,
        value: propertyData.values?.[0]?.value || propertyData.value,
    };
};

export enum OperationType {
    ADD,
    EDIT,
}

export const DATA_PRODUCT = 'DATA_PRODUCT';
export const GLOSSARY_TERM = 'GLOSSARY_TERM';
export const DOMAIN = 'DOMAIN';

type AddOrEditPropertyProps = {
    urn?: string;
    type: string;
    isModalVisible: boolean;
    setIsModalVisible: (event) => void;
    operationType?: OperationType;
    editModalData?: any;
    refetch: () => void;
    customPropertyKeys?: string[];
};

export const AddOrEditProperty = ({
    urn,
    type,
    isModalVisible,
    setIsModalVisible,
    operationType,
    editModalData,
    refetch,
    customPropertyKeys = [],
}: AddOrEditPropertyProps) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);

    const [updateGlossaryProperty] = useUpdateGlossaryPropertyMutation();
    const [useUpdateEntityProperty] = useUpdateEntityPropertyMutation();
    const [updateDomainPropertyMutation] = useUpdateDomainPropertyMutation();
    const [updateDataProductPropertyMutation] = useUpdateDataProductPropertyMutation();

    const operationTxt = operationType === OperationType.ADD ? t('Add New') : t('Edit');
    const isEdit = operationType === OperationType.EDIT;

    const checkForDuplicate = (key: string) => {
        if (operationType === OperationType.ADD && key) {
            setIsDuplicate(customPropertyKeys.includes(key.toLowerCase().trim()));
        } else {
            setIsDuplicate(false);
        }
    };

    const handleFormChange = () => {
        const hasErrors = form.getFieldsError().some((field) => field.errors.length > 0);
        const values = form.getFieldsValue();
        const hasValues = values.key?.trim() && values.value?.trim();
        setCanSubmit(!hasErrors && hasValues && !isDuplicate);
    };

    useEffect(() => {
        if (isEdit) {
            form.setFieldsValue(extractPropertyFormValues(editModalData));
            setTimeout(handleFormChange, 0);
        } else {
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editModalData, form, isEdit]);

    const onClose = () => {
        setIsModalVisible(false);
        setIsDuplicate(false);
        setCanSubmit(false);
    };

    const onAfterClose = () => {
        // Checks if the operation type is equal to the ADD operation type.
        if (!isEdit) {
            form.resetFields();
        } else {
            // Ensure we're setting both key and value from the editModalData
            form.setFieldsValue(extractPropertyFormValues(editModalData));
        }
    };

    const getAddOrEditMutationFunc = (entityType) => {
        switch (entityType) {
            case DATA_PRODUCT:
                return updateDataProductPropertyMutation;
            case GLOSSARY_TERM:
                return updateGlossaryProperty;
            case DOMAIN:
                return updateDomainPropertyMutation;
            default:
                return useUpdateEntityProperty;
        }
    };

    const getEntityTxt = (entityType) => {
        switch (entityType) {
            case DATA_PRODUCT:
                return t('Data Product');
            case GLOSSARY_TERM:
                return t('Glossary');
            case DOMAIN:
                return t('Domain');
            default:
                return t('Entity');
        }
    };

    /**
     * Adds or updates a glossary property.
     */
    const addOrUpdateGlossaryProperty = async () => {
        const values = await form.validateFields();

        // Sanitize the property values before saving
        const sanitizedValues = {
            key: sanitizeToPlainText(values.key),
            value: sanitizeToPlainText(values.value),
        };

        const addOrEditMutation = getAddOrEditMutationFunc(type);
        const entityTxt = getEntityTxt(type);

        const text = operationType === OperationType.ADD ? t('Added') : t('Updated');
        setLoading(true);

        // Checks if the operation type is equal to the ADD operation type.
        try {
            await addOrEditMutation({
                variables: {
                    input: {
                        urn: urn || '',
                        customProperties: [sanitizedValues],
                    },
                },
            });
            refetch();
            setLoading(false);
            message.destroy();
            message.success({ content: t('Property {{text}} Successfully', { text }), duration: 2 });
            onClose();
        } catch (e: unknown) {
            message.destroy();
            setLoading(false);
            if (e instanceof Error) {
                message.error({
                    content: t('Failed to {{operationTxt}} {{entityTxt}} Property: \n {{message}}', {
                        operationTxt,
                        entityTxt,
                        message: e.message || '',
                    }),
                    duration: 2,
                });
            }
        }
    };

    return (
        <Modal
            title={t('{{operationTxt}} Property', { operationTxt })}
            visible={isModalVisible}
            onCancel={onClose}
            afterClose={onAfterClose}
            destroyOnClose
            className="f-modal"
            centered
            footer={
                <>
                    <Button type="primary" onClick={addOrUpdateGlossaryProperty} disabled={loading || !canSubmit}>
                        {operationType === OperationType.ADD ? t('Add') : t('Save')}
                    </Button>
                    <Button type="default" onClick={onClose}>
                        {t('Cancel')}
                    </Button>
                </>
            }
        >
            <Form form={form} layout="vertical" colon={false} requiredMark={false} onFieldsChange={handleFormChange}>
                <Form.Item
                    name="key"
                    className="mb-0"
                    label={
                        <Typography.Text strong>
                            {t('Name')} <span className="f-color-risk-very-high">*</span>
                        </Typography.Text>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('A Name is required.'),
                        },
                        { min: 2, max: 50, message: t('Name must be between 2 and 50 characters') },
                    ]}
                    validateStatus={isDuplicate ? 'error' : undefined}
                    help={isDuplicate ? t('A property with this name already exists.') : undefined}
                >
                    <Input
                        placeholder={t('A name for the property')}
                        autoFocus
                        disabled={operationType === OperationType.EDIT}
                        onChange={(e) => checkForDuplicate(e.target.value)}
                    />
                </Form.Item>
                <Typography.Paragraph type="secondary" className="py-1">
                    {t('Give the property a convenient name.')}
                </Typography.Paragraph>

                <Form.Item
                    name="value"
                    className="mb-0"
                    label={
                        <Typography.Text strong>
                            {t('Value')} <span className="f-color-risk-very-high">*</span>
                        </Typography.Text>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('A value is required.'),
                        },
                    ]}
                >
                    <Input placeholder={t('The value for the property')} />
                </Form.Item>
                <Typography.Paragraph type="secondary" className="py-1">
                    {t('Assign the property a value.')}
                </Typography.Paragraph>
            </Form>
        </Modal>
    );
};
